export enum ContentType {
  POINTS = 'points',
  QUOTE = 'quote',
  PARAGRAPH = 'paragraph',
  IMAGE = 'image',
  LINE = 'line'
}

export type CaseStudy = {
  id: string
  slug: string
  title: string
  color?: string
  image: string
  description?: string
  testimonial: {
    text: string
    author: {
      name: string
      designation?: string
      image: string
    }
    chain: {
      name: string
      image: string
    }
    products?: string[]
  }
  impact: {
    title: string
    icon?: string
    bgcolor?: string
  }[]
  sections: {
    title: string
    subtitle?: string
    image?: string
    content: {
      type: ContentType
      src?: string
      text?:
        | string
        | string[]
        | {
            text: string
            icon: string | any
          }[]
      gradient_start?: string
      gradient_end?: string
    }[]
  }[]
}

export const marcos: CaseStudy = {
  id: 'marcos-pizza-running-3pd-ops-on-autopilot',
  slug: 'marcos-pizza-running-3pd-ops-on-autopilot',
  title: 'Marco’s Pizza: Running 3PD Ops on Autopilot',
  color: '#FDD294',
  image: '/case-study/marcos/banner.png',
  testimonial: {
    text: 'Loop has become a wonderful autopilot for us in running our 3P delivery business, providing incredible granularity into the inner workings of our 3PD business.',
    author: {
      name: 'Marc Seelinger',
      designation: 'Franchisee, Marco’s Pizza',
      image: '/case-study/marcos/author.png'
    },
    chain: {
      name: 'Marco’s Pizza',
      image: '/case-study/marcos/logo.png'
    }
  },
  impact: [
    {
      title: '25% growth in sales, 40% growth in profits',
      icon: 'trending_up',
      bgcolor: '#FFE8DE'
    },
    {
      title: '43% increase in payouts after 3P fees (latest period)',
      icon: 'payments',
      bgcolor: '#7C339D29'
    },
    {
      title: '9% higher 3PD margins',
      icon: 'percent',
      bgcolor: '#58F42836'
    },
    {
      title: 'Marketing spends reduced by 33% while sales grew',
      icon: 'savings',
      bgcolor: '#12E6F124'
    }
  ],
  sections: [
    {
      title: 'The Problem',
      subtitle: 'Before Loop, Marco’s was flying blind:',
      // image: '/case-study/marcos/problem.png',
      content: [
        {
          type: ContentType.POINTS,
          text: [
            {
              text: 'Third-party platforms advised their marketing spend.',
              icon: 'campaign'
            },
            {
              text: 'No visibility into <b>marketplace rankings</b> or <b>retention</b>',
              icon: 'visibility_off'
            },
            {
              text: 'Marketing was broad, excessive, and inefficient.',
              icon: 'money_off'
            },
            {
              text: 'Margins lagged behind industry benchmarks.',
              icon: 'trending_down'
            }
          ]
        },
        {
          type: ContentType.QUOTE,
          text: 'Loop has significantly taken the load of 3PD business management off our shoulders.',
          gradient_start: '#C885FF1F',
          gradient_end: '#6427FF3B'
        }
      ]
    },
    {
      title: 'The Solution',
      subtitle: 'Loop’s autopilot approach flipped the script.',
      // image: '/case-study/marcos/solution.png',
      content: [
        {
          type: ContentType.IMAGE,
          src: '/case-study/marcos/banner1.png'
        },
        {
          type: ContentType.POINTS,
          text: [
            {
              text: "<b>Ads vs Promos</b> decisions were now driven by Loop's <b>Marketplace Ranking insights</b>",
              icon: 'insights'
            },
            {
              text: 'Precision targeting became possible with <b>customer retention analytics</b>',
              icon: 'person_search'
            },
            {
              text: 'Critical workstreams across <b>marketing, finance, and ops</b> were automated.',
              icon: 'auto_awesome'
            }
          ]
        },
        {
          type: ContentType.QUOTE,
          text: 'Loop takes care of critical functions and workstreams around marketing, finance, and operations.',
          gradient_start: '#CEFF8640',
          gradient_end: '#26A13F4D'
        }
      ]
    }
  ]
}

export const carolinas: CaseStudy = {
  id: 'subs-of-the-carolinas-simplifying-third-party-ops-at-scale',
  slug: 'subs-of-the-carolinas-simplifying-third-party-ops-at-scale',
  title: 'Subs of the Carolinas: Simplifying Third-Party Ops at Scale',
  color: '#aee8f5',
  image: '/case-study/carolinas/banner.png',
  testimonial: {
    text: 'Given the substantial number of locations and transactions, it became challenging to manage the distinction between sales, fees, and deposits.',
    author: {
      name: 'Zach James',
      designation: 'CFO, Subs of the Carolinas',
      image: '/case-study/carolinas/author.png'
    },
    chain: {
      name: 'Subs of the Carolinas',
      image: '/case-study/carolinas/logo.png'
    }
  },
  impact: [
    {
      title: 'Consolidated third-party data in one dashboard',
      icon: 'dashboard',
      bgcolor: '#FFE8DE'
    },
    {
      title: 'Automated reconciliation process, no manual journal entries',
      icon: 'sync',
      bgcolor: '#7C339D29'
    },
    {
      title: 'Improved marketing ROI visibility per location',
      icon: 'visibility',
      bgcolor: '#58F42836'
    },
    {
      title: 'Operational clarity across 80+ stores',
      icon: 'store',
      bgcolor: '#12E6F124'
    }
  ],
  sections: [
    {
      title: 'The Problem',
      content: [
        {
          type: ContentType.PARAGRAPH,
          text: 'As Subs of the Carolinas scaled to 80+ Jersey Mike’s locations, third-party delivery exploded. But fragmented data made it <b>impossible to track sales, fees, and deposits cleanly.</b>'
        },
        {
          type: ContentType.PARAGRAPH,
          text: 'Manual reconciliations drained time. Marketing decisions lacked real performance visibility.'
        },
        {
          type: ContentType.QUOTE,
          text: 'Loop is a time-saving solution, financial savings, and comprehensive data analytics.',
          gradient_start: '#aee8f530',
          gradient_end: '#aee8f5'
        }
      ]
    },
    {
      title: 'The Solution',
      content: [
        {
          type: ContentType.IMAGE,
          src: '/case-study/carolinas/banner1.png'
        },
        {
          type: ContentType.PARAGRAPH,
          text: "Loop's <b>TruROI</b> gave Zach’s team a consolidated, filterable view of 3PD data — unlocking insights on customer trends, store-level weaknesses, and marketing ROI."
        },
        {
          type: ContentType.QUOTE,
          text: 'Loop enables us to identify aspects like customer trends, store downtime, and marketing results. From an operational standpoint, this is invaluable.',
          gradient_start: '#CEFF8640',
          gradient_end: '#26A13F4D'
        },
        {
          type: ContentType.LINE
        },
        {
          type: ContentType.IMAGE,
          src: '/case-study/carolinas/banner2.png'
        },
        {
          type: ContentType.PARAGRAPH,
          text: 'With <b>Balance</b>, weekly journal-ready files synced directly with their accounting system — no more manual entry.'
        },
        {
          type: ContentType.QUOTE,
          text: 'Loop provides imported journal entries customized for our A/R process. This saves us hours every week.',
          gradient_start: '#C885FF1F',
          gradient_end: '#6427FF3B'
        }
      ]
    }
  ]
}
